import DashboardLayout from '@/components/DashboardLayout'
import {
  ManagerAccessCard,
  type PermissionModule,
} from '@/components/ManagerAccessCard'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { CpfInput } from '@/components/ui/cpf-input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { router } from '@inertiajs/react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useState } from 'react'
import axios from 'axios'
import { Info, Loader2 } from 'lucide-react'

interface User {
  id: number
  name: string
  email: string
  phone: string
  role: string
  document?: string
  birth_date?: string | null
  foreign_national?: boolean
  zip_code?: string | null
  street?: string | null
  street_number?: string | null
  complement?: string | null
  neighborhood?: string | null
  address_city?: string | null
  address_state?: string | null
  // Só chegam quando o alvo é gerente — ver ManagerAccessCard.
  panel_access?: boolean
  full_access?: boolean
  permissions?: string[]
  commission_enabled?: boolean
  commission_percentage?: number
}

interface School {
  id: number
  name: string
  fiscal_enabled?: boolean
}

interface UsersEditProps {
  user: User
  school: School
  context?: 'students' | 'team'
  permission_catalog?: Record<string, PermissionModule[]>
}

const userSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z
    .string()
    .min(6, 'Telefone é obrigatório')
    .regex(/^[+]?[0-9\s\-\(\)]+$/, 'Telefone inválido'),
  document: z.string().optional(),
  birth_date: z.string().optional(),
  foreign_national: z.boolean(),
  role: z.enum(['student', 'teacher', 'manager'], {
    message: 'Selecione uma função',
  }),
  // Endereço fiscal (opcional no cadastro; exigido apenas na emissão da nota).
  zip_code: z.string().optional(),
  street: z.string().optional(),
  street_number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  address_city: z.string().optional(),
  address_state: z.string().optional(),
})

type UserFormData = z.infer<typeof userSchema>

export default function UsersEdit({
  user,
  school,
  context = 'students',
  permission_catalog,
}: UsersEditProps) {
  const isStudentContext = context === 'students'

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      phone: user.phone,
      document: user.document || '',
      birth_date: user.birth_date || '',
      foreign_national: user.foreign_national ?? false,
      role: user.role as 'student' | 'teacher' | 'manager',
      zip_code: user.zip_code || '',
      street: user.street || '',
      street_number: user.street_number || '',
      complement: user.complement || '',
      neighborhood: user.neighborhood || '',
      address_city: user.address_city || '',
      address_state: user.address_state || '',
    },
  })

  const isForeign = useWatch({ control, name: 'foreign_national' })
  const [isLoadingCep, setIsLoadingCep] = useState(false)

  const handleCepSearch = async () => {
    const cleanCep = (getValues('zip_code') || '').replace(/\D/g, '')
    if (cleanCep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos')
      return
    }

    setIsLoadingCep(true)
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      )

      if (response.data.erro) {
        toast.error('CEP não encontrado')
        return
      }

      setValue('street', response.data.logradouro || '')
      setValue('neighborhood', response.data.bairro || '')
      setValue('address_city', response.data.localidade || '')
      setValue('address_state', response.data.uf || '')

      toast.success('Endereço preenchido pelo CEP')
    } catch {
      toast.error('Erro ao buscar CEP. Verifique se o CEP está correto.')
    } finally {
      setIsLoadingCep(false)
    }
  }

  const onSubmit = async (data: UserFormData) => {
    const payload = { ...data }

    if (payload.foreign_national) {
      payload.document = ''
      // Mesmo tratamento do cadastro: o endereço fiscal é o do tomador na NFS-e
      // (CEP, UF, município do IBGE) e estrangeiro não tem nenhum dos três. Sai
      // da tela e sai do payload — inclusive limpando o que já estava salvo em
      // quem foi marcado como estrangeiro depois de cadastrado.
      payload.zip_code = ''
      payload.street = ''
      payload.street_number = ''
      payload.complement = ''
      payload.neighborhood = ''
      payload.address_city = ''
      payload.address_state = ''
    }

    router.patch(
      `/users/${user.id}`,
      { user: payload },
      {
        onSuccess: () => {
          toast.success('Usuário atualizado com sucesso!')
        },
        onError: (errors) => {
          const errorMessage = Object.values(errors).flat().join(', ')
          toast.error(errorMessage || 'Erro ao atualizar usuário')
        },
      },
    )
  }

  const backPath = isStudentContext ? '/students' : '/team'

  return (
    <DashboardLayout schoolName={school?.name}>
      <div className="p-8 space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {isStudentContext ? 'Editar Aluno' : 'Editar Membro da Equipe'}
          </h2>
          <p className="text-muted-foreground">
            Atualize as informações do usuário
          </p>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>
              {isStudentContext
                ? 'Informações do Aluno'
                : 'Informações do Membro'}
            </CardTitle>
            <CardDescription>
              Atualize os dados cadastrais do usuário
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="João Silva"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@exemplo.com"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* CPF + toggle Estrangeiro — antes do Telefone */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="document">CPF</Label>
                  {isStudentContext && (
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="foreign_national"
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Estrangeiro
                      </label>
                      <Controller
                        name="foreign_national"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="foreign_national"
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
                {!isForeign && (
                  <Controller
                    name="document"
                    control={control}
                    render={({ field }) => (
                      <CpfInput {...field} id="document" />
                    )}
                  />
                )}
                {errors.document && (
                  <p className="text-sm text-destructive">
                    {errors.document.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone *</Label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => <PhoneInput {...field} id="phone" />}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Data de Nascimento</Label>
                <Input
                  id="birth_date"
                  type="date"
                  {...register('birth_date')}
                />
                {errors.birth_date && (
                  <p className="text-sm text-destructive">
                    {errors.birth_date.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Função *</Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma função" />
                      </SelectTrigger>
                      <SelectContent>
                        {isStudentContext ? (
                          <SelectItem value="student">Aluno</SelectItem>
                        ) : (
                          <>
                            <SelectItem value="teacher">Professor</SelectItem>
                            <SelectItem value="manager">Gerente</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.role && (
                  <p className="text-sm text-destructive">
                    {errors.role.message}
                  </p>
                )}

                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {isStudentContext ? (
                    <p>
                      <strong>Aluno:</strong> Pode visualizar suas aulas e
                      progresso
                    </p>
                  ) : (
                    <>
                      <p>
                        <strong>Professor:</strong> Pode gerenciar turmas e
                        avaliar alunos
                      </p>
                      <p>
                        <strong>Gerente:</strong> Pode visualizar relatórios e
                        gerenciar operações
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Endereço fiscal — só quando a escola emite nota fiscal, e nunca
                  para estrangeiro (mesma regra do cadastro: o endereço aqui é o
                  do tomador na NFS-e, com CEP e município do IBGE, que
                  estrangeiro não tem). */}
              {isStudentContext && school?.fiscal_enabled && !isForeign && (
                <div className="space-y-4 rounded-md border p-4">
                  <div>
                    <p className="text-sm font-medium">
                      Endereço (nota fiscal)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Usado na emissão da nota fiscal. Só é obrigatório no
                      momento de emitir.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">CEP</Label>
                      <div className="flex gap-2">
                        <Input
                          id="zip_code"
                          placeholder="00000-000"
                          {...register('zip_code', {
                            onBlur: handleCepSearch,
                          })}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCepSearch}
                          disabled={isLoadingCep}
                        >
                          {isLoadingCep ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Buscar'
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street">Rua / Logradouro</Label>
                      <Input id="street" {...register('street')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street_number">Número</Label>
                      <Input
                        id="street_number"
                        {...register('street_number')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="complement">Complemento</Label>
                      <Input id="complement" {...register('complement')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro</Label>
                      <Input id="neighborhood" {...register('neighborhood')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address_city">Cidade</Label>
                      <Input id="address_city" {...register('address_city')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address_state">UF</Label>
                      <Input
                        id="address_state"
                        maxLength={2}
                        placeholder="CE"
                        {...register('address_state')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isStudentContext && (
                <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 p-3 text-sm text-blue-800 dark:text-blue-300">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    A senha deste aluno é o telefone que foi cadastrado no
                    momento da criação (somente números). Se não souber qual é,
                    use o campo abaixo para redefinir.
                  </p>
                </div>
              )}

              {/* Definir a senha de outra pessoa daqui saía caro: o campo valia sobre
                  qualquer usuário da escola, inclusive o dono, e era o primeiro degrau
                  da tomada de conta. Agora o dono dispara o e-mail e quem escolhe a
                  senha é o titular. */}
              <div className="border-t pt-4 space-y-3">
                <div>
                  <p className="text-sm font-medium">Senha</p>
                  <p className="text-xs text-muted-foreground">
                    A senha inicial é o telefone cadastrado. Para trocar, envie
                    o e-mail de redefinição — quem escolhe a nova senha é o
                    próprio usuário.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    router.post(
                      `/users/${user.id}/reset_password`,
                      {},
                      {
                        preserveScroll: true,
                        onSuccess: () =>
                          toast.success('E-mail de redefinição enviado.'),
                        onError: () =>
                          toast.error('Não foi possível enviar o e-mail.'),
                      },
                    )
                  }
                >
                  Enviar e-mail de redefinição
                </Button>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit(backPath)}
                >
                  Cancelar
                </Button>
                <Button variant="default" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Só para gerente: professor não entra no painel, e o dono não configura a si
            mesmo. O card salva por conta própria, num endpoint que não aceita dado
            pessoal junto. */}
        {user.role === 'manager' && permission_catalog && (
          <div className="max-w-2xl mt-6">
            <ManagerAccessCard user={user} catalog={permission_catalog} />
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
